import {useState} from 'react';
import {useMutation} from '@apollo/client';
import moment from 'moment';
import {toast} from 'react-toastify';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import DBModal from 'components/modal/modal';
import RequirementRow from './RequirementRow';
import styles from './schemeUserLeaderboard.module.scss';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {AUTHORIZE_REWARD} from '../resolvers';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';

const SchemeUserLeaderboardDetailModal = ({
  entry,
  visible,
  onClose,
  scheme,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [authorizeReward, {loading}] = useMutation(AUTHORIZE_REWARD, {
    onError: error => toast.error(error.message),
    errorPolicy: 'all',
  });
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Leaderboard),
  );

  if (!entry || !visible) {
    return null;
  }

  const user = entry.user;
  const details = entry.eligibilityDetails || {};
  const completedTrades = details.numberOfTransactions ?? 0;
  const wins = details.YTDWins ?? 0;
  const maxRewards = details.maxNumberOfRewards ?? 5;
  const requiredDabaPro = scheme?.operationConfig?.requireDabaPro;
  const minNumberOfTransactions =
    scheme?.operationConfig?.minNumberOfTransactions;
  const hasPro = details.hasDabaPro;
  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : 'Unknown user';
  const userId = user?.accountId || '-';
  const competitionPeriod =
    entry.cycleStartDate && entry.cycleEndDate
      ? `${moment(entry.cycleStartDate).format('MMM D, YYYY')} - ${moment(
          entry.cycleEndDate,
        ).format('MMM D, YYYY')}`
      : '-';
  const isWithinLimit = wins <= maxRewards;
  const rewardInfo = entry.reward?.userReward;
  const formattedRewardType = rewardInfo?.type
    ? rewardInfo.type.replaceAll('_', ' ')
    : '—';
  const isWithinMinNumberOfTransactions =
    minNumberOfTransactions === 0 ||
    (minNumberOfTransactions && completedTrades >= minNumberOfTransactions);
  // Format currency values
  const formatCurrency = value => {
    if (value == null) return '—';
    return `CFA ${Number(value).toLocaleString()}`;
  };

  // Format percentage values
  const formatPercentage = value => {
    if (value == null) return '—';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${Number(value).toFixed(2)}%`;
  };

  const formatRewardValue = () => {
    if (!rewardInfo) {
      return '—';
    }
    if (rewardInfo.rewardUnits) {
      return `${rewardInfo.rewardUnits} unit${
        rewardInfo.rewardUnits > 1 ? 's' : ''
      } of ${rewardInfo.ticker || 'Any'} @ CFA ${
        rewardInfo.stockPrice?.toLocaleString() ?? '--'
      }`;
    }
    if (rewardInfo.cashValue) {
      return `CFA ${rewardInfo.cashValue.toLocaleString()}`;
    }
    if (rewardInfo.amount) {
      const isFeeWaiver = rewardInfo.type === 'TRANSACTION_FEE_WAIVER';
      return isFeeWaiver
        ? `${rewardInfo.amount} (${rewardInfo.percentage ?? '--'}%)`
        : `${rewardInfo.amount}`;
    }
    return '—';
  };

  const requirementRows = [
    {
      label: 'Daba Pro subscription active for the full competition week',
      status: requiredDabaPro ? Boolean(hasPro) : true,
      value: requiredDabaPro
        ? hasPro
          ? 'Requirement met'
          : 'Subscription missing'
        : 'Requirement met',
    },
    {
      label: `Minimum of ${minNumberOfTransactions} completed trades`,
      status: isWithinMinNumberOfTransactions,
      value: `${completedTrades} trades completed`,
    },
    {
      label: `Maximum of ${maxRewards} wins per calendar year`,
      status: isWithinLimit,
      value: `${wins} wins recorded this year`,
    },
  ];

  const handleAuthorizeReward = () => {
    if (entry?.reward?.status !== 'PENDING') {
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleConfirmAuthorize = () => {
    if (!entry?.reward?.id) {
      toast.error('Unable to authorize reward. Missing reward reference.');
      setIsConfirmOpen(false);
      return;
    }

    authorizeReward({
      variables: {
        adminUpdateUserRewardStatusId: entry.reward.id,
        status: 'APPROVED',
      },
    })
      .then(result => {
        setIsConfirmOpen(false);
        const payload = result?.data?.adminUpdateUserRewardStatus;
        if (payload?.__typename === 'Error') {
          toast.error(payload.message);
        } else {
          toast.success('Reward authorized successfully.');
          onClose();
        }
      })
      .catch(error => {
        setIsConfirmOpen(false);
        toast.error(
          error?.message || 'Something went wrong while authorizing reward.',
        );
      });
  };

  const modalContent = (
    <div className={styles.modalContent}>
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>User Performance & Reward Review</h2>
        <div className={styles.userInfo}>
          <span className={styles.userName}>{userName}</span>
          <span className={styles.userId}>ID: {userId}</span>
        </div>
      </div>

      <div className={styles.alertsContainer}>
        <div
          className={
            entry.isEligibleForReward
              ? styles.alertSuccess
              : styles.alertWarning
          }>
          {entry.isEligibleForReward
            ? '✓ Meets all eligibility requirements'
            : '✗ Does not meet all eligibility requirements'}
        </div>
        {details.maxNumberOfRewards && (
          <div className={styles.alertHelper}>
            {isWithinLimit
              ? 'Within annual reward limit'
              : 'Exceeded annual reward limit'}
          </div>
        )}
      </div>

      <div className={styles.contentGrid}>
        <div className={styles.leftColumn}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Performance</div>
            <div className={styles.compactGrid}>
              <div className={styles.compactItem}>
                <span className={styles.compactLabel}>Rank</span>
                <span className={styles.compactValue}>
                  {entry.rank ? `#${entry.rank}` : '—'}
                </span>
              </div>
              <div className={styles.compactItem}>
                <span className={styles.compactLabel}>Daba Pro</span>
                <span className={styles.compactValue}>
                  {user?.proSubscription?.hasProSubscription ? 'Yes' : 'No'}
                </span>
              </div>
              <div className={styles.compactItem}>
                <span className={styles.compactLabel}>Wins (YTD)</span>
                <span className={styles.compactValue}>
                  {wins} / {maxRewards}
                </span>
              </div>
              <div className={styles.compactItem}>
                <span className={styles.compactLabel}>Period</span>
                <span className={styles.compactValue}>{competitionPeriod}</span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>ROI Performance</div>
            <div className={styles.metricsList}>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>ROI Change</span>
                <span
                  className={`${styles.metricValue} ${
                    entry.roiChange >= 0
                      ? styles.percentPositive
                      : styles.percentNegative
                  }`}>
                  {formatPercentage(entry.roiChange)}
                </span>
              </div>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>Current ROI</span>
                <span className={styles.metricValue}>
                  {formatPercentage(entry.currentROI)}
                </span>
              </div>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>Starting ROI</span>
                <span className={styles.metricValue}>
                  {formatPercentage(entry.startingROI)}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Portfolio Value</div>
            <div className={styles.metricsList}>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>Current Value</span>
                <span className={styles.metricValue}>
                  {formatCurrency(entry.currentPortfolioValue)}
                </span>
              </div>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>Starting Value</span>
                <span className={styles.metricValue}>
                  {formatCurrency(entry.startingPortfolioValue)}
                </span>
              </div>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>Portfolio Change</span>
                <span
                  className={`${styles.metricValue} ${
                    entry.portfolioPercentageChange >= 0
                      ? styles.percentPositive
                      : styles.percentNegative
                  }`}>
                  {formatPercentage(entry.portfolioPercentageChange)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Investment Details</div>
            <div className={styles.metricsList}>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>
                  Current Total Invested
                </span>
                <span className={styles.metricValue}>
                  {formatCurrency(entry.currentTotalInvested)}
                </span>
              </div>
              <div className={styles.metricRow}>
                <span className={styles.metricLabel}>
                  Starting Total Invested
                </span>
                <span className={styles.metricValue}>
                  {formatCurrency(entry.startingTotalInvested)}
                </span>
              </div>
              {entry.calculatedAt && (
                <div className={styles.metricRow}>
                  <span className={styles.metricLabel}>Calculated At</span>
                  <span className={styles.metricValue}>
                    {moment(entry.calculatedAt).format('MMM D, YYYY HH:mm')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {rewardInfo && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Reward</div>
              <div className={styles.rewardCard}>
                <div className={styles.rewardItem}>
                  <span className={styles.rewardLabel}>Type</span>
                  <span className={styles.rewardValue}>
                    {formattedRewardType}
                  </span>
                </div>
                <div className={styles.rewardItem}>
                  <span className={styles.rewardLabel}>Value</span>
                  <span className={styles.rewardValue}>
                    {formatRewardValue()}
                  </span>
                </div>
                {rewardInfo.ticker && (
                  <div className={styles.rewardItem}>
                    <span className={styles.rewardLabel}>Ticker</span>
                    <span className={styles.rewardValue}>
                      {rewardInfo.ticker}
                    </span>
                  </div>
                )}
                {rewardInfo.percentage && (
                  <div className={styles.rewardItem}>
                    <span className={styles.rewardLabel}>Percentage</span>
                    <span className={styles.rewardValue}>
                      {rewardInfo.percentage}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Eligibility</div>
            <div className={styles.requirementsCompact}>
              {requirementRows.map(row => (
                <RequirementRow key={row.label} {...row} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.modalFooter}>
        <SubmitButton
          disabled={
            entry?.reward?.status !== 'PENDING' || !tabOperations?.update
          }
          label="Authorize Reward"
          handleClick={handleAuthorizeReward}
        />
      </div>
    </div>
  );

  return (
    <>
      <DBModal
        isOpen={visible}
        handleClose={onClose}
        destroyOnClose
        width={900}
        content={modalContent}
      />
      <ConfirmModal
        title="Confirm Reward Authorization"
        openModal={isConfirmOpen}
        setOpenModal={setIsConfirmOpen}
        confirmText={
          <span>
            Are you sure you want to authorize this reward for{' '}
            <strong>{userName}</strong>?
          </span>
        }
        loading={loading}
        handleOperation={handleConfirmAuthorize}
      />
    </>
  );
};

export default SchemeUserLeaderboardDetailModal;
